import React, {useEffect, useState} from 'react';
import {Form, Stack, Tab, Tabs} from 'react-bootstrap';
import {useParams} from 'react-router-dom';
import {AlgoRobotResultComponent} from './algo-robot-result.component';
import "react-datetime/css/react-datetime.css";
import moment from "moment";
import Chart from "react-apexcharts";
import './algo-robot-detail.page.scss';
import {
    DatetimePicker,
    DateUtils,
    DI,
    DisplayUtils,
    LepriconService, Loading,
    OrderPerformance,
    OrderStatistics,
    SelectedDate
} from "shared-lib";

const lepriconService = DI.get(LepriconService);

const CHART_OPTIONS = {
    chart: {
        toolbar: {
            show: true,
            offsetX: 0,
            offsetY: 0,
            tools: {
                download: true,
                selection: true,
                zoom: true,
                zoomin: true,
                zoomout: true,
                pan: true,
            }
        }
    },
    stroke: {
        curve: 'smooth',
    },
    dataLabels: {
        enabled: false
    },
    tooltip: {
        enabled: true,
    },
    zoom: {
        enabled: true,
        type: 'x',
        autoScaleYaxis: false,
        zoomedArea: {
            fill: {
                color: '#90CAF9',
                opacity: 0.4
            },
            stroke: {
                color: '#0D47A1',
                opacity: 0.4,
                width: 1
            }
        }
    },
} as any;

export function AlgoRobotDetailPage(): React.ReactElement {

    const [inProgress, setInProgress] = useState(false);
    const [period, setPeriod] = useState({
        startDate: moment('2019-01-01', 'YYYY-MM-DD'),
        endDate: DateUtils.getEndOfToday()
    } as SelectedDate);
    const [originalData, setOriginalData] = useState(null as OrderStatistics);
    const [displayedSymbol, setDisplayedSymbol] = useState('all');
    const [data, setData] = useState({} as OrderStatistics);
    const params = useParams();
    const [chartData, setChartData] = useState(null);
    const packageDetail = lepriconService.getAlgoRobotPackageDetailById(params.packageId);


    const updateData = () => {
        if (!inProgress) {
            setInProgress(true);
            lepriconService.getOrderStatistics(period.startDate, period.endDate, packageDetail.alertName).then(resp => {
                setOriginalData(resp);
                updateChart(resp.performances);
            });
        }
    }

    const updateChart = (performances: OrderPerformance[]) => {
        if (performances.length > 0) {
            const inspector = performances[0];
            const dates = inspector.values.map(value => value.date);
            const series = performances.map(performance => ({
                name: performance.code,
                data: performance.values.map(value => value.percentage.toFixed(2))
            }));
            const options = {...CHART_OPTIONS, xaxis: {categories: dates}};
            setChartData({options, series});
        }
    }

    useEffect(() => {
        updateData()
    }, [params, period]);


    useEffect(() => {
        if (originalData != null) {
            if (displayedSymbol === 'all') {
                setData(originalData);
                updateChart(originalData.performances);
            } else {
                const orderStatistics = originalData.orderStatisticsBySymbols.find(orderWithSymbol => orderWithSymbol.code === displayedSymbol) as any;
                setData(orderStatistics);
                updateChart(orderStatistics.performances)
            }
            setInProgress(false);
        }
    }, [originalData, displayedSymbol]);

    const handleSymbolChange = (event) => {
        setInProgress(true);
        setDisplayedSymbol(event.target.value);
    };

    return (
        <div className='sl-loading-container'>
            <Loading show={inProgress}/>
            <Stack direction="horizontal" className="justify-content-between">
                <Stack direction="vertical">
                    <h3 className="mb-2">{packageDetail.name}</h3>
                    <h5> {packageDetail.description}</h5>
                </Stack>

                <h4></h4>
                <Stack direction="horizontal">
                    <Stack className="me-2">
                        <div className="fw-bold ps-1">Semboller:</div>
                        <Form.Select size="sm" onChange={handleSymbolChange} style={{width: '100px'}}>
                            <option value="all">Tümü</option>
                            {originalData && originalData.codes.map(symbol =>
                                <option value={symbol}>{symbol}</option>
                            )}

                        </Form.Select>
                    </Stack>
                    <DatetimePicker className="datepicker" initialSelectedDate={period} onChange={date => {
                        setPeriod({...date})
                    }}/>
                </Stack>
            </Stack>

            <div className="rounded bg-white mt-5 p-3 box-shadow">
                <h2 className="mb-4">Performans Özeti</h2>
                <Stack direction="horizontal" gap={3}>
                    <span><strong>Toplam Yatırım Tutarı:</strong> {DisplayUtils.displayFormattedNumberValue(data.initialBalance)}</span>
                    <span><strong>Sembol Başına Yatırılan Tutar:</strong> {DisplayUtils.displayFormattedNumberValue(data.initialBalanceForOneSymbol)}</span>
                    <span><strong>Son Bakiye:</strong> {DisplayUtils.displayFormattedNumberValue(data.lastBalance)}</span>
                    <span><strong>Net Getiri Yüzdesi:</strong> %{DisplayUtils.displayFormattedNumberValue(data.closedTradesPLPercentage)}</span>
                </Stack>
                <div style={{height: '500px'}}>
                    {chartData && (
                        <Chart
                            options={chartData.options}
                            series={chartData.series}
                            type="line"
                            height={500}
                        />
                    )}
                </div>
                <Stack direction="horizontal">
                    <Stack gap={2} className="mx-2">
                        <span
                            className="d-flex justify-content-between">Net Getiri Yüzdesi:<strong>%{DisplayUtils.displayFormattedNumberValue(data.closedTradesPLPercentage)}</strong></span>
                        <span
                            className="d-flex justify-content-between">Toplam Net Getiri:<strong>{DisplayUtils.displayFormattedNumberValue(data.closedTradesPL)}</strong></span>
                        <span
                            className="d-flex justify-content-between">Kazanç Katsayısı:<strong>{DisplayUtils.displayFormattedNumberValue(data.profitFactor)}</strong></span>
                        <span
                            className="d-flex justify-content-between">Kapatılmış İşlem Sayısı:<strong>{DisplayUtils.displayRawValue(data.closedTradesCount)}</strong></span>
                        <span
                            className="d-flex justify-content-between">Kârlı İşlem Sayısı:<strong>{DisplayUtils.displayRawValue(data.profitableTradesCount)}</strong></span>
                        <span
                            className="d-flex justify-content-between">Zararlı İşlem Sayısı:<strong>{DisplayUtils.displayRawValue(data.lossTradesCount)}</strong></span>
                        <span
                            className="d-flex justify-content-between">Kârlı İşlem Yüzdesi:<strong>%{DisplayUtils.displayFormattedNumberValue(data.profitableTradesPercentage)}</strong></span>
                    </Stack>
                    <Stack gap={2} className="mx-2">
                        <span
                            className="d-flex justify-content-between">Son 1 Ay Getirisi:<strong>{DisplayUtils.displayPercentageValue(data.lastOneMonthPLPercentage)}</strong></span>
                        <span
                            className="d-flex justify-content-between">Son 3 Ay Getirisi:<strong>{DisplayUtils.displayPercentageValue(data.lastThreeMonthsPLPercentage)}</strong></span>
                        <span
                            className="d-flex justify-content-between">Son 6 Ay Getirisi:<strong>{DisplayUtils.displayPercentageValue(data.lastSixMonthsPLPercentage)}</strong></span>
                        <span
                            className="d-flex justify-content-between">Son 1 Yıl Getirisi:<strong>{DisplayUtils.displayPercentageValue(data.lastTwelveMonthsPLPercentage)}</strong></span>
                        <span
                            className="d-flex justify-content-between">En Kârlı İşlemin Yüzde Getirisi:<strong>{DisplayUtils.displayPercentageValue(data.maxProfitPercentage)}</strong></span>
                        <span
                            className="d-flex justify-content-between">En Zararlı İşlemin Yüzde Getirisi:<strong>{DisplayUtils.displayPercentageValue(data.maxLossPercentage)}</strong></span>
                        <span
                            className="d-flex justify-content-between">Maksimum Sermaye Erime Yüzdesi:<strong>{DisplayUtils.displayPercentageValue(data.maxDDPercentage)}</strong></span>
                    </Stack>
                    <Stack gap={2} className="mx-2">
                        <span
                            className="d-flex justify-content-between">Brüt Kâr:<strong>{DisplayUtils.displayFormattedNumberValue(data.grossSumProfit)}</strong></span>
                        <span
                            className="d-flex justify-content-between">Brüt Zarar:<strong>{DisplayUtils.displayFormattedNumberValue(data.grossSumLoss)}</strong></span>
                        <span
                            className="d-flex justify-content-between">Ortalama Ardışık Kârlı İşlem Sayısı:<strong>{DisplayUtils.displayFormattedNumberValue(data.averageConsecutiveWinsCount)}</strong></span>
                        <span
                            className="d-flex justify-content-between">Ortalama Ardışık Zararlı İşlem Sayısı:<strong>{DisplayUtils.displayFormattedNumberValue(data.averageConsecutiveLossesCount)}</strong></span>
                        <span
                            className="d-flex justify-content-between">Maksimum Ardışık Kârlı İşlem Sayısı:<strong>{DisplayUtils.displayRawValue(data.maximumConsecutiveWinsCount)}</strong></span>
                        <span
                            className="d-flex justify-content-between"> Maksimum Ardışık Zararlı İşlem Sayısı:<strong>{DisplayUtils.displayRawValue(data.maximumConsecutiveLossesCount)}</strong></span>
                        <span
                            className="d-flex justify-content-between">Ortalama Pozisyon Taşıma Süresi:<strong>{DisplayUtils.displayTextValue(moment.duration(data.avgTradeDurationInMinutes, 'minutes').asDays().toFixed(2) + ' gün')}</strong></span>
                    </Stack>
                </Stack>

            </div>

            <div className=" rounded bg-white mt-5 p-3 box-shadow d-flex flex-column">
                <Tabs
                    defaultActiveKey="open"
                    className="mb-3"
                >
                    <Tab eventKey="open" title="AÇIK POZİSYONLAR">
                        <AlgoRobotResultComponent orders={data?.openOrders || []}/>
                    </Tab>
                    <Tab eventKey="closed" title="KAPALI POZİSYONLAR">
                        <AlgoRobotResultComponent orders={data?.closedOrders || []}/>
                    </Tab>
                </Tabs>
            </div>
        </div>
    );
}
